<ng-container *ngIf="pins$ | async as pins">
	<div *ngIf="pins.length">
		<div class="flex items-center py-3 pl-10 gap-6">
			<div class="flex pr-3 items-center gap-4">
				<h3 class="font-bold">Summary | PIN: {{ selectedPin.pin }}</h3>
				<ui-button
					data-intercom-target="pin-view-show-hide"
					[label]="(showDetails ? 'Hide' : 'Show') + ' Summary'"
					(click)="toggleShowDetails()"
					preset="ICON_DEFAULT"
					[icon]="showDetails ? 'eye-off' : 'eye'"
					[testHook]="testHooks.SearchNewPinButton"
				/>
			</div>
		</div>
		<div
			class="flex flex-1"
			[@openCloseWithState]="showDetails ? 'open' : 'closed'"
		>
			<div
				class="border-y border-r border-grey-lighter p-5 min-w-[300px] max-w-[350px] w-1/4 overflow-y-auto"
			>
				<div *ngFor="let parentPin of pins" class="flex flex-col">
					<div
						class="flex mb-2 p-2 items-center justify-between rounded-xl border border-grey-lighter hover:bg-grey-lightest cursor-pointer"
						[ngClass]="{
							'bg-grey-lightest': selectedPin.pin === parentPin.pin,
						}"
						(click)="selectPin(parentPin)"
						[testHook]="testHooks.SelectPinDiv"
					>
						<div class="mr-2 flex-1 flex items-center gap-2">
							<span
								class="text-lg whitespace-nowrap"
								[ngClass]="{
									'text-grey-dark': selectedPin.pin === parentPin.pin,
									'text-grey': selectedPin.pin !== parentPin.pin,
								}"
							>
								{{ parentPin?.pin }}
							</span>
							<app-icon
								*ngIf="parentPin?.pin === primaryPin"
								matTooltip="Primary"
								class="ml-1 text-sm text-yellow-300"
								[icon]="ICON.STAR"
								[testHook]="testHooks.StarIcon"
							></app-icon>
						</div>

						<div
							class="rounded-2xl bg-grey-lightest px-2 flex gap-2 items-center border border-grey-lighter cursor-help"
							*ngIf="parentPin?.partLot"
						>
							<app-icon
								[icon]="ICON.PART_PARCEL"
								class="text-gray-500 text-md"
								matTooltip="part lot"
								(click)="viewMap()"
								[testHook]="testHooks.PartParcelIcon"
							></app-icon>
							<p class="text-sm text-grey-dark whitespace-nowrap">Part Lot</p>
						</div>
						<div
							class="rounded-2xl bg-grey-lightest px-2 flex gap-2 items-center border border-grey-lighter cursor-help"
							*ngIf="parentPin?.sameAdjacentOwners"
						>
							<app-icon
								class="text-gray-500 text-md"
								[icon]="ICON.PARTIES"
								matTooltip="similar names identified"
								(click)="goToCompare()"
								[testHook]="testHooks.PartiesIcon"
							></app-icon>
							<p class="text-sm text-grey-dark whitespace-nowrap">Adjacent</p>
						</div>
					</div>

					<div
						class="border-l p-2 my-2 border-grey-lighter"
						*ngIf="parentPin.adjacentPins.length > 0"
					>
						<p class="text-md text-grey pb-1">Adjacent PINs</p>

						<div
							*ngFor="let lot of parentPin.adjacentPins"
							class="flex flex-1 max-w-[280px] ml-2 my-1 p-2 items-center justify-between rounded-xl border border-grey-lighter hover:bg-grey-lightest cursor-pointer"
							[ngClass]="{ 'bg-grey-lightest': selectedPin.pin === lot.pin }"
							(click)="selectPin(lot)"
							[testHook]="testHooks.SelectPinDiv1"
						>
							<span
								class="text-lg"
								[ngClass]="{
									'text-grey-dark': selectedPin.pin === lot.pin,
									'text-grey': selectedPin.pin !== lot.pin,
								}"
							>
								{{ lot.pin }}
							</span>

							<div
								class="rounded-2xl bg-grey-lightest px-2 flex gap-2 items-center border border-grey-lighter cursor-help"
								*ngIf="lot?.partLot"
							>
								<app-icon
									[icon]="ICON.PART_PARCEL"
									class="text-gray-500 text-md"
									matTooltip="part lot"
									(click)="viewMap()"
									[testHook]="testHooks.PartParcelIcon1"
								></app-icon>
								<p class="text-sm text-grey-dark whitespace-nowrap">Part Lot</p>
							</div>

							<div
								class="rounded-2xl bg-grey-lightest px-2 flex gap-2 items-center border border-grey-lighter cursor-help"
								*ngIf="lot?.sameAdjacentOwners"
							>
								<app-icon
									class="text-gray-500 text-md"
									[icon]="ICON.PARTIES"
									matTooltip="similar names identified"
									(click)="goToCompare()"
									[testHook]="testHooks.PartiesIcon1"
								></app-icon>
								<p class="text-sm text-grey-dark whitespace-nowrap">Adjacent</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				class="border-y bg-white border-grey-lighter p-5 flex flex-1 flex-col w-3/4 min-w-[660px]"
			>
				<div class="flex justify-end items-center">
					<div class="flex [&>*]:pb-4">
						<ui-button
							*ngIf="isOverview"
							[disabled]="!selectedPin?.parent"
							(click)="goToSync()"
							label="Sync details"
							preset="EXTRA_LOW_EMPHASIS"
							[testHook]="testHooks.SyncDetailsButton"
						/>
						<ui-button
							*ngIf="selectedPin?.pin?.map && showSimilarNames"
							[disabled]="!selectedPin.parent"
							(click)="goToCompare()"
							label="Similar names"
							preset="EXTRA_LOW_EMPHASIS"
							[testHook]="testHooks.SimilarNamesButton"
						/>
						<div *ngIf="isOverview" class="ml-4">
							<ui-button
								label="View adjacent lot"
								*ngIf="selectedPin?.parent && selectedPin?.map"
								(click)="viewMap()"
								preset="EXTRA_LOW_EMPHASIS"
								[testHook]="testHooks.ViewAdjacentLotButton"
							/>
							<ui-button
								label="Purchase map"
								*ngIf="selectedPin?.parent && !selectedPin?.map"
								[disabled]="!selectedPin?.parent"
								(click)="purchaseMap()"
								teranetLoginGuard
								preset="EXTRA_LOW_EMPHASIS"
								[tooltip]="
									!selectedPin?.parent
										? 'Only parent PINs adjacent maps are available for purchase'
										: null
								"
								[testHook]="testHooks.PurchaseMapButton"
							/>
						</div>
					</div>
				</div>
				<div class="grid grid-cols-6 gap-6" #summaryContainer>
					<span
						class="font-bold mr-3 col-span-1"
						*ngIf="isOverview && selectedPin?.parent"
					>
						Search status
					</span>
					<app-teranet-pin-status
						class="col-span-5"
						*ngIf="isOverview && selectedPin?.parent"
					></app-teranet-pin-status>
					<span class="font-bold mr-3 w-64 col-span-1"> Address</span>
					<div class="flex col-span-5">
						<span
							*ngIf="selectedPin?.address?.unit_type?.length > 0"
							matTooltip="Unit Type"
							>({{ selectedPin?.address?.unit_type }})</span
						>
						<span *ngIf="selectedPin?.address?.unit_number?.length > 0"
							>{{ selectedPin?.address.unit_number }}-</span
						>
						<span
							>{{ selectedPin?.address?.street_number }}
							{{ selectedPin?.address?.street_name | capitalize }}
							{{ selectedPin?.address?.street_suffix | capitalize }}</span
						>
						<span *ngIf="selectedPin?.address?.area?.length > 0"
							>, {{ selectedPin?.address?.area }}</span
						>
					</div>
					<div class="font-bold mr-3 col-span-1">Property Description</div>
					<div class="col-span-5 pr-10">
						<div class="flex flex-col">
							<div
								class="transition-all duration-300 ease-in-out overflow-hidden"
								[style.max-height]="dynamicHeight"
								[ngClass]="{
									'bg-gradient-to-b from-black to-white text-transparent bg-clip-text':
										!showFullDescription && expandable,
								}"
								#descriptionContent
							>
								{{ selectedPin?.legal_desc }}
							</div>

							<span
								*ngIf="expandable"
								class="text-green w-fit cursor-pointer hover:underline"
								(click)="toggleShowFullDescription()"
							>
								{{ showFullDescription ? '...Show Less' : '...Read More' }}
							</span>
						</div>
					</div>

					<div class="font-bold mr-3 w-64 col-span-1">Owner(s)</div>
					<div class="col-span-5" *ngIf="selectedPin?.parcels?.length > 0">
						<span
							*ngFor="let owner of selectedPin?.parcels[0]?.owners"
							class="mr-4"
							[ngClass]="{ 'bg-gray-200': owner.sameName }"
						>
							{{ owner.name | capitalize }}
							<span *ngIf="owner.capacity?.length > 0"
								>({{ owner.capacity }})</span
							>
							<app-icon
								*ngIf="owner.sameName"
								matTooltip="Common Names Detected"
								class="text-lg text-gray-400 ml-3"
								[icon]="ICON.PARTIES"
								[testHook]="testHooks.PartiesIcon2"
							></app-icon>
						</span>
					</div>

					<div *ngIf="isOverview" class="flex flex-1 gap-2 col-span-6">
						<app-checkbox
							type="clear"
							[ngModel]="purchaseDeleted"
							(ngModelChange)="setPurchaseDeleted($event)"
							[label]="'Include deleted instruments'"
							[testHook]="testHooks.PurchaseDeletedCheckbox"
						></app-checkbox>
						<app-button
							size="small"
							(click)="purchaseParcel()"
							teranetLoginGuard
							[loading]="
								selectedPin?.pin &&
								parcelPurchaseState &&
								parcelPurchaseState[selectedPin.pin]?.state ===
									purchaseState.PURCHASING
							"
							[testHook]="testHooks.PurchaseParcelButton"
							>{{
								selectedPin?.parcels?.length > 0
									? 'Re-purchase parcel'
									: 'Purchase parcel'
							}}</app-button
						>
					</div>

					<div class="grid grid-cols-6 col-span-6 items-center">
						<div class="font-bold mr-3 w-64 col-span-1">Parcels</div>

						<div class="col-span-5">
							<div class="flex items-center">
								<div
									*ngFor="let parcel of selectedPin?.parcels"
									[matTooltip]="
										'Latest Instrument Number:' + parcel?.latestInstrumentNumber
									"
									class="mr-5 flex items-center border rounded-xl px-4 py-2 cursor-pointer hover:bg-grey-lightest"
									(click)="openPDF(parcel)"
									[testHook]="testHooks.OpenPdfDiv"
								>
									<div
										class="bg-red-lighter rounded-xl p-2 flex items-center justify-center mr-3"
									>
										<app-icon
											class="text-2xl text-red-500"
											[icon]="ICON.PDF"
											[testHook]="testHooks.PdfIcon"
										></app-icon>
									</div>

									<div class="flex flex-col">
										<span class="text-md text-grey-dark"
											>{{ selectedPin.pin }}.pdf</span
										>
										<p class="text-sm text-grey-light">
											Last Modified {{ parcel?.dateSearched | date: 'short' }}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</ng-container>
